import type { LoyaltyTierUpgradeClaimResult } from 'api/loyalty/requestLoyaltyTierUpgradeClaim'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import HeadData from 'components/Common/HeadData'
import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useLoyaltyLandingPageBenefitCopy from 'loyalty/hooks/useLoyaltyLandingPageBenefitCopy'
import useTierUpgradeCampaign from 'loyalty/hooks/useTierUpgradeCampaign'
import useStatusMatchCampaign from 'loyalty/hooks/useStatusMatchCampaign'
import LoyaltyLandingPageBenefitsTableSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageBenefitsTableSection'
import LoyaltyLandingPageDiscoverTiersSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageDiscoverTiersSection'
import LoyaltyLandingPageFaqSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageFaqSection'
import LoyaltyLandingPageHowItWorksSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageHowItWorksSection'
import React, { lazy, Suspense, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import TierUpgradeCampaignHero from './TierUpgradeCampaignHero'
import TierUpgradeCampaignTierCards from './TierUpgradeCampaignTierCards'
import TierUpgradeCampaignBenefitsShowcase from './TierUpgradeCampaignBenefitsShowcase'
import TierUpgradeCampaignPromoModal from './TierUpgradeCampaignPromoModal'
import TierUpgradeCampaignSuccessModal from './TierUpgradeCampaignSuccessModal'
import TierUpgradeCampaignTerms from './TierUpgradeCampaignTerms'

const StatusMatchLandingPage = lazy(() => import('loyalty/pages/statusMatch/StatusMatchLandingPage'))

function TierUpgradeCampaignPage() {
  const intl = useIntl()
  const { campaignSlug = '' } = useParams<'campaignSlug'>()
  const statusMatch = useStatusMatchCampaign(campaignSlug)
  const campaign = useTierUpgradeCampaign(campaignSlug)
  const [benefitCopy] = useLoyaltyLandingPageBenefitCopy()
  const [loyaltyConfig] = useLoyaltyConfig()

  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)

  const ctaText = useMemo(() => {
    if (isLoggedIn) {
      return intl.formatMessage({
        id: 'q-NkUijJQaU2ohMNamA5',
        defaultMessage: 'Activate {programName} Status',
        description: 'CTA button text for logged-in users on tier upgrade campaign',
      }, { programName: getLoyaltyProgramDisplayName() })
    }
    return intl.formatMessage({
      id: 'xu06GsEaNXXfySYAeY2s',
      defaultMessage: 'Login to activate',
      description: 'CTA button text for logged-out users on tier upgrade campaign',
    })
  }, [intl, isLoggedIn])

  const joinModalPrimaryCta = intl.formatMessage({
    id: 'ZQlZxOs_CeHcomZKCni_',
    defaultMessage: 'Activate free membership',
    description: 'Primary button text on the loyalty join modal shown to existing users',
  })

  const onCtaClick = useCallback(async() => {
    if (!campaign) return

    if (!isLoggedIn) {
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal initialMode="loyaltyTierUpgradeCampaignLogin" />)
      if (!loginResult?.loggedIn) return
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
      return
    }

    if (!hasLoyaltyAccount) {
      const joinConfirmed = await showModal<boolean>(<LoyaltyJoinModal
        onAfterClose={undefined}
        primaryActionCta={joinModalPrimaryCta}
      />)
      if (!joinConfirmed) return
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }

    const result = await showModal<LoyaltyTierUpgradeClaimResult>(<TierUpgradeCampaignPromoModal campaign={campaign} />)
    if (result?.claimed) {
      const tierUpgradeAction = result.actions.find(a => a.type === 'TIER_UPGRADE' && a.status === 'COMPLETED')
      const newTier = (tierUpgradeAction?.result?.newTier ?? campaign.tiers[0]) as App.LoyaltyTier
      showModal(<TierUpgradeCampaignSuccessModal newTier={newTier} />)
    }
  }, [isLoggedIn, hasLoyaltyAccount, showModal, campaign, dispatch, joinModalPrimaryCta])

  if (statusMatch) {
    const { hero } = statusMatch.config
    // Keep HeadData outside the lazy page so SSR emits share meta for non-JS crawlers.
    // ICU template so locales can reorder brand/title/subtitle (e.g. Arabic RTL).
    const headTitle = intl.formatMessage({
      id: 'tx4qgBv-mRu2bTeEIhFW',
      defaultMessage: '{brandName} - {title} {subtitle}',
      description: 'Browser tab and social share title for Status Match pages combining brand with hero title and subtitle',
    }, {
      brandName: config.title,
      title: hero.title,
      subtitle: hero.subtitle,
    })
    return <>
      <HeadData
        title={headTitle}
        imageId={hero.imageId}
      />
      <Suspense fallback={null}>
        <StatusMatchLandingPage campaignConfig={statusMatch} />
      </Suspense>
    </>
  }

  if (!campaign) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  const isSingleTier = campaign.tiers.length === 1
  const singleTierBenefits = isSingleTier && loyaltyConfig
    ? loyaltyConfig.regionTierBenefits[campaign.tiers[0]]
    : undefined

  return <div>
    <TierUpgradeCampaignHero
      campaign={campaign}
      onCtaClick={onCtaClick}
      ctaText={ctaText}
      tierBenefits={singleTierBenefits}
    />
    {!isSingleTier && <TierUpgradeCampaignTierCards campaign={campaign} onCtaClick={onCtaClick} ctaText={ctaText} />}
    <TierUpgradeCampaignBenefitsShowcase
      benefitCopy={benefitCopy}
      onCtaClick={onCtaClick}
      ctaText={ctaText}
    />
    <LoyaltyLandingPageDiscoverTiersSection />
    <LoyaltyLandingPageBenefitsTableSection
      onJoin={onCtaClick}
      ctaText={ctaText}
      compact
    />
    <LoyaltyLandingPageHowItWorksSection onJoin={onCtaClick} ctaText={ctaText} />
    <LoyaltyLandingPageFaqSection slug="societe-faqs" />
    <TierUpgradeCampaignTerms slug={campaign.termsSlug} />
  </div>
}

export default TierUpgradeCampaignPage
